import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Bot, 
  TrendingUp, 
  Users2,
  Zap,
  Trophy,
  Clock,
  Target,
  Gamepad2,
  Heart
} from "lucide-react";

export function FeatureShowcase() {
  const features = [
    {
      icon: Users,
      title: "Real-Time Multiplayer",
      description: "Play against friends and players worldwide with lightning-fast connections",
      details: ["Instant matchmaking", "Private rooms", "Spectator mode", "Live chat"],
      color: "teen-green",
      badge: "Most Popular",
      stats: "Always Available"
    },
    {
      icon: Bot,
      title: "Smart AI Opponents",
      description: "Challenge AI bots with unique personalities and adaptive difficulty",
      details: ["4 difficulty levels", "Personality traits", "Adaptive learning", "Custom strategies"],
      color: "teen-blue",
      badge: "AI Powered",
      stats: "8 unique bots"
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Watch your skills grow with detailed analytics and achievements",
      details: ["ELO rating system", "Game analysis", "Performance graphs", "Achievement badges"],
      color: "teen-purple",
      badge: "Pro Feature",
      stats: "Track Your Growth"
    },
    {
      icon: Users2,
      title: "Community Hub",
      description: "Join tournaments, forums, and connect with chess enthusiasts",
      details: [
        { text: "Weekly tournaments", comingSoon: true },
        { text: "Discussion forums", comingSoon: true },
        { text: "Friend system", comingSoon: false },
        { text: "Leaderboards", comingSoon: false }
      ],
      color: "teen-gold",
      badge: "Community",
      secondaryBadge: "Coming Soon",
      stats: "Join The Community"
    }
  ];

  const quickFeatures = [
    { icon: Clock, text: "Multiple time controls", color: "teen-green" },
    { icon: Target, text: "Hatch Evolution", color: "teen-blue" },
    { icon: Gamepad2, text: "Mobile friendly", color: "teen-purple" },
    { icon: Heart, text: "Beginner friendly", color: "teen-gold" }
  ];

  return (
    <div className="space-y-12">
      {/* Main Features Grid */}
      <div className="space-y-6">
        <div className="text-center space-y-3">
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teen-purple via-teen-pink to-teen-cyan bg-clip-text text-transparent">
            What Makes ChessHatch Special? ✨
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A fresh, modern chess platform built with cutting-edge technology for today's players
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <Card 
              key={index}
              variant="vibrant"
              className={`group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 bg-gradient-to-br from-${feature.color}/30 to-${feature.color}/10 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <feature.icon className={`w-6 h-6 text-${feature.color}`} />
                    </div>
                    <div>
                      <CardTitle className={`text-xl text-${feature.color} mb-1`}>
                        {feature.title}
                      </CardTitle>
                    <div className="flex gap-2">
                        <Badge 
                          className={`text-xs bg-${feature.color}/20 text-${feature.color} border-${feature.color}/30`}
                        >
                          {feature.badge}
                        </Badge>
                        {feature.secondaryBadge && (
                          <Badge className="text-xs bg-muted text-muted-foreground border-border">
                            🚧 {feature.secondaryBadge}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <CardDescription className="text-base mt-3">
                  {feature.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {feature.details.map((detail, idx) => {
                    const isObject = typeof detail === 'object' && detail !== null;
                    const text = isObject ? detail.text : detail;
                    const isComingSoon = isObject && detail.comingSoon;
                    
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-2 text-sm ${
                          isComingSoon 
                            ? 'text-muted-foreground/50 line-through' 
                            : 'text-muted-foreground'
                        }`}
                      >
                        <div className={`w-1.5 h-1.5 ${
                          isComingSoon ? 'bg-muted-foreground/30' : `bg-${feature.color}`
                        } rounded-full`}></div>
                        {text}
                        {isComingSoon && (
                          <span className="text-[10px] text-muted-foreground/50 no-underline">(soon)</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className={`text-center py-2 px-3 bg-gradient-to-r from-${feature.color}/10 to-${feature.color}/5 rounded-lg border border-${feature.color}/20`}>
                  <span className={`text-sm font-semibold text-${feature.color}`}>
                    {feature.stats}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Quick Features Bar - Vibrant gradient */}
      <div className="bg-gradient-to-r from-teen-purple/15 via-teen-pink/15 to-teen-cyan/15 rounded-2xl p-6 border border-primary/20 shadow-vibrant">
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-teen-purple to-teen-pink bg-clip-text text-transparent">
            Plus so much more... 🎉
          </h3>
          
          <div className="flex flex-wrap justify-center gap-3">
            {quickFeatures.map((item, index) => (
              <div 
                key={index}
                className={`flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-white/10 backdrop-blur-sm rounded-full border border-${item.color}/30 hover:border-${item.color}/60 hover:shadow-md transition-all duration-300`}
              >
                <item.icon className={`w-4 h-4 text-${item.color}`} />
                <span className={`text-sm text-${item.color} font-medium`}>{item.text}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            Everything you need for an amazing chess experience! 🚀
          </p>
        </div>
      </div>
    </div>
  );
}